/**
 * CompletenessMeter — a live "how complete is this listing" bar for the seller
 * editor header (PIM Wave P2, Task 7).
 *
 * Renders the LIVE client estimate (`computeCompleteness`, which equals the
 * backend's persisted `completeness_score` — that column is not exposed on any
 * response DTO, so the live estimate stands in; flagged in the build log) and,
 * when required fields are still empty, lists them as jump-links (click →
 * `onJumpToField(slug)` focuses + scrolls the field).
 */
import { useMemo, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import {
  flattenFields,
  pickText,
  type AttributeFormValues,
  type FormSchemaResponse,
} from "@/features/seller-dashboard/attributes/formSchema";
import {
  computeCompleteness,
  requiredMissingFields,
} from "@/features/seller-dashboard/attributes/completeness";

export interface CompletenessMeterProps {
  /** The active primary-category schema, or null while it loads / none chosen. */
  schema: FormSchemaResponse | null;
  values: AttributeFormValues;
  language: string;
  onJumpToField?: (fieldSlug: string) => void;
}

/** Bar tone by score band — never color-only (the % is always shown). */
function toneClass(score: number): string {
  if (score >= 100) return "bg-[var(--g-success)]";
  if (score >= 50) return "bg-[var(--g-primary)]";
  return "bg-[var(--g-warn)]";
}

export function CompletenessMeter({
  schema,
  values,
  language,
  onJumpToField,
}: CompletenessMeterProps): ReactElement | null {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);

  const score = useMemo(
    () => (schema ? computeCompleteness(schema, values) : 0),
    [schema, values],
  );
  const missing = useMemo(
    () => (schema ? requiredMissingFields(schema, values) : []),
    [schema, values],
  );

  // No meter until a category with scored fields is loaded.
  if (!schema || flattenFields(schema).length === 0) return null;

  return (
    <section
      aria-label={t("attributes.completeness.label")}
      data-testid="completeness-meter"
      className="flex flex-col gap-2 rounded-[var(--g-radius)] border border-border bg-card p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">
          {t("attributes.completeness.label")}
        </span>
        <span className="text-sm font-semibold tabular-nums text-foreground" data-testid="completeness-score">
          {t("attributes.completeness.percent", { value: score })}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("attributes.completeness.label")}
        className="h-2 w-full overflow-hidden rounded-full bg-secondary"
      >
        <div
          className={["h-full rounded-full transition-[width]", toneClass(score)].join(" ")}
          style={{ width: `${score}%` }}
        />
      </div>
      {missing.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">
            {t("attributes.completeness.missingTitle", { count: missing.length })}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {missing.map((field) => (
              <button
                key={field.field_slug}
                type="button"
                onClick={() => onJumpToField?.(field.field_slug)}
                className="inline-flex items-center rounded-full border border-[var(--g-error)]/40 px-2.5 py-0.5 text-xs font-medium text-[var(--g-error)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {pickText(field.label, language)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default CompletenessMeter;
